import type { RepeatableTreeNode } from "@/features/dynamic-variables/repeatable/scope";
import type { RepeatableField } from "@/features/dynamic-variables/repeatable/types";

/**
 * One row of a repeatable group: a value per field key, plus — for a group with
 * nested groups — an array of the nested group's rows under the nested key.
 */
export type RepeatableRow = Record<string, unknown>;

/**
 * Build one empty row record: `""` per field key and `[]` per direct nested
 * group key (grandchildren are seeded when their own row is created).
 */
export const createRepeatableRow = (
	fields: RepeatableField[],
	children?: RepeatableTreeNode[],
	/** Org variable definitions by name: a new row starts from each field's
	 * fallback value, like a top-level variable does. */
	definitions?: Map<string, { default?: unknown }>,
): RepeatableRow => ({
	...Object.fromEntries(
		fields.map((f) => [f.key, defaultFor(definitions?.get(f.key)?.default)]),
	),
	...Object.fromEntries(
		(children ?? []).map((child) => [child.repeatable.key, []]),
	),
});

/** A fallback value usable as a row cell: strings and numbers pass, lists
 * (multiselect) pass, anything else (objects, null) becomes "". */
const defaultFor = (value: unknown): unknown => {
	if (typeof value === "string" || typeof value === "number") return value;
	// Copy: rows built from one definition must not share a multiselect list.
	if (Array.isArray(value)) return [...value];
	return "";
};
